import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/DetailForm";
import { DetailBadge } from "@/features/ui/detail/DetailBadge";
import { DetailContent } from "@/features/ui/detail/DetailContent";
import { DetailField } from "@/features/ui/detail/DetailField";
import { DetailHeader } from "@/features/ui/detail/DetailHeader";
import { DetailInformTitle } from "@/features/ui/detail/DetailInformTitle";
import { DetailLabel } from "@/features/ui/detail/DetailLabel";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { DetailLinkWrapper } from "@/features/ui/detail/DetailLinkWrapper";
import { DetailTitle } from "@/features/ui/detail/DetailTitle";
import { setTransportationStatus } from "@/utils/scmDataStatus";
import timeUtil from "@/utils/timeUtil";
import { trackingStatusColor } from "@/utils/workStatus";
import { Checkbox, Space } from "@mantine/core";
import { TransportationGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { useTranslation } from "react-i18next";

const DetailTransportation = Object.assign(DetailHeader, {
  Title: DetailTitle,
  Inform: DetailInformTitle,
  Field: DetailField,
  Label: DetailLabel,
  Link: DetailLink,
  LinkWrapper: DetailLinkWrapper,
  Badge: DetailBadge,
  Content: DetailContent,
  CheckBox: Checkbox,
});

export interface TransportationDetailHeaderProps {
  data: TransportationGet200ResponseRowsInner | undefined;
}

export const TransportationDetailHeader = (params: TransportationDetailHeaderProps) => {
  const { data } = params;
  const { t } = useTranslation();
  const { openModal } = useModal();

  return (
    <DetailTransportation>
      <DetailTransportation.Title>{`[배차] ${data?.id}`}</DetailTransportation.Title>
      <Space h={8} />
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("transportation.status")}</DetailTransportation.Label>
        <DetailTransportation.Content>
          <DetailTransportation.Badge
            variant="filled"
            color={trackingStatusColor[data?.status as keyof typeof trackingStatusColor]}
          >
            {setTransportationStatus(data?.status ?? "")}
          </DetailTransportation.Badge>
        </DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("transportation.direction")}</DetailTransportation.Label>
        <DetailTransportation.Content>
          <DetailTransportation.Badge color={data?.direction === "INBOUND" ? "blue" : "red"}>
            {t(data?.direction ?? "")}
          </DetailTransportation.Badge>
        </DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("transportation.code")}</DetailTransportation.Label>
        <DetailTransportation.Content>{data?.code}</DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("transportation.scheduledAt")}</DetailTransportation.Label>
        <DetailTransportation.Content>
          {data?.scheduledAt !== null ? timeUtil(data?.scheduledAt ?? "") : ""}
        </DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("partner.name")}</DetailTransportation.Label>
        <DetailTransportation.Content>
          <DetailTransportation.Link
            onClick={() => {
              openModal(
                <PartnerDetailForm partnerCode={data?.partnerCode} />,
                null,
                t("common.detail", { item: t("partner.partner") }), // 파트너 상세
              );
            }}
          >
            {data?.partner?.name}
          </DetailTransportation.Link>
        </DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("transportation.carNumber")}</DetailTransportation.Label>
        <DetailTransportation.Content>{data?.vehicle?.plateNumber}</DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("transportation.driver")}</DetailTransportation.Label>
        <DetailTransportation.Content>{data?.vehicle?.driver}</DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>
          {t("transportation.numberOfWmsLogs")}
        </DetailTransportation.Label>
        <DetailTransportation.Content>
          {!data?.wmsLogs?.length ? "-" : data.wmsLogs.length}
        </DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("routing.descript")}</DetailTransportation.Label>
        <DetailTransportation.Content>{data?.description ?? ""}</DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("info.creatorUserCode")}</DetailTransportation.Label>
        <DetailTransportation.Content> {data?.creatorUserCode ?? ""}</DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("info.createdAt")}</DetailTransportation.Label>
        <DetailTransportation.Content>
          {data?.createdAt ? timeUtil(data?.createdAt ?? "") : ""}
        </DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("info.updaterUserCode")}</DetailTransportation.Label>
        <DetailTransportation.Content> {data?.updaterUserCode ?? ""}</DetailTransportation.Content>
      </DetailTransportation.Field>
      <DetailTransportation.Field>
        <DetailTransportation.Label>{t("info.updatedAt")}</DetailTransportation.Label>
        <DetailTransportation.Content>
          {data?.updatedAt ? timeUtil(data?.updatedAt) : ""}
        </DetailTransportation.Content>
      </DetailTransportation.Field>
    </DetailTransportation>
  );
};
